/**
 * Interface definitions for UI components
 * Each theme must implement these methods
 */

/**
 * @typedef {Object} KnobOptions
 * @property {number} min - Minimum value
 * @property {number} max - Maximum value
 * @property {number} value - Initial value
 * @property {string} [label] - Label text
 * @property {boolean} [logarithmic] - Use logarithmic scale
 * @property {number} [step] - Step increment
 * @property {string} [unit] - Unit suffix (Hz, %, etc.)
 */

/**
 * @typedef {Object} SliderOptions
 * @property {number} min - Minimum value
 * @property {number} max - Maximum value
 * @property {number} value - Initial value
 * @property {string} [label] - Label text
 * @property {boolean} [vertical] - Vertical orientation
 * @property {number} [step] - Step increment
 */

/**
 * @typedef {Object} StepButtonOptions
 * @property {boolean} active - Initial active state
 * @property {number} index - Step index (0-15)
 * @property {string} [type] - Button type: 'gate', 'accent', 'slide'
 * @property {string} [color] - Button color when active
 */

/**
 * @typedef {Object} SelectOptions
 * @property {Array<{value: string, label: string}>} options - Available options
 * @property {string} value - Initial selected value
 * @property {string} [label] - Label text
 */

/**
 * Base component interface - all components must have these
 */
export class IComponent {
    /**
     * @param {HTMLElement} container - DOM element to render into
     * @param {Object} options - Component-specific options
     */
    constructor(container, options) {
        if (new.target === IComponent) {
            throw new Error('IComponent is abstract and cannot be instantiated directly');
        }
        this.container = container;
        this.options = options;
        this._changeCallbacks = [];
    }

    /** Get current value */
    getValue() {
        throw new Error('getValue() must be implemented');
    }

    /** Set current value */
    setValue(value) {
        throw new Error('setValue() must be implemented');
    }

    /** Register change callback */
    onChange(callback) {
        this._changeCallbacks.push(callback);
        return this; // Allow chaining
    }

    /** Emit change to all callbacks */
    _emitChange(value) {
        this._changeCallbacks.forEach(cb => cb(value));
    }

    /** Clean up resources */
    destroy() {
        this._changeCallbacks = [];
        if (this.element && this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
        }
    }
}

/**
 * Knob interface - rotary control
 */
export class IKnob extends IComponent {
    /**
     * @param {HTMLElement} container
     * @param {KnobOptions} options
     */
    constructor(container, options) {
        super(container, options);
    }
}

/**
 * Slider interface - linear control (horizontal or vertical)
 */
export class ISlider extends IComponent {
    /**
     * @param {HTMLElement} container
     * @param {SliderOptions} options
     */
    constructor(container, options) {
        super(container, options);
    }
}

/**
 * StepButton interface - sequencer step
 */
export class IStepButton extends IComponent {
    /**
     * @param {HTMLElement} container
     * @param {StepButtonOptions} options
     */
    constructor(container, options) {
        super(container, options);
    }

    /** Get active state */
    isActive() {
        throw new Error('isActive() must be implemented');
    }

    /** Set active state */
    setActive(active) {
        throw new Error('setActive() must be implemented');
    }

    /** Highlight as current step */
    setCurrent(isCurrent) {
        throw new Error('setCurrent() must be implemented');
    }
}

/**
 * Select interface - dropdown
 */
export class ISelect extends IComponent {
    /**
     * @param {HTMLElement} container
     * @param {SelectOptions} options
     */
    constructor(container, options) {
        super(container, options);
    }
}

/**
 * LED interface - indicator light
 */
export class ILED extends IComponent {
    /**
     * @param {HTMLElement} container
     * @param {Object} options
     * @param {string} [options.color] - LED color
     * @param {boolean} [options.on] - Initial state
     */
    constructor(container, options) {
        super(container, options);
    }

    /** Turn on */
    on() {
        throw new Error('on() must be implemented');
    }

    /** Turn off */
    off() {
        throw new Error('off() must be implemented');
    }

    /** Toggle state */
    toggle() {
        throw new Error('toggle() must be implemented');
    }
}
